import { isBaseGraphKeyCompatible } from "@features/run/RunGroupDialog/helpers"
import { RunGroupFormValues } from "@features/run/RunGroupDialog/RunGroupForm"
import { useLazyGetRunGroupByKeyQuery } from "@store/services/runService"
import { useLazyGetRunResultQuery } from "@store/services/runService"
import { useLazyGetModelQuery } from "@store/services/modelsService"
import { compareLinkedGraphDates } from "@utils/dateValidation"
import { RunGroupStatus } from "@custom-types/modelRun"
import { GraphDto } from "@custom-types/graphs"
import { parseISO } from "date-fns"
import { useCallback, useRef, useState } from "react"
import { useFormContext } from "react-hook-form"

/** parse from UTC */
const parseModelDate = (str: string) => parseISO(str.replace(/-(\d{2}:\d{2}:\d{2})$/, "T$1Z"))

const DEBOUNCE_MS = 1000

type UseLinkedRunValidationOptions = {
  fieldName: `previous_run_key.${number}.runId`
  graphKey: string | undefined
  graph: GraphDto
  maxLag: number
}

export const useLinkedRunValidation = ({
  fieldName,
  graphKey,
  graph,
  maxLag,
}: UseLinkedRunValidationOptions) => {
  const { setError, clearErrors, getValues } = useFormContext<RunGroupFormValues>()
  const [isValidating, setIsValidating] = useState(false)
  const [errors, setErrors] = useState<string[]>([])
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [triggerGetRunGroup] = useLazyGetRunGroupByKeyQuery()
  const [triggerGetRunResult] = useLazyGetRunResultQuery()
  const [triggerGetModel] = useLazyGetModelQuery()

  const runValidation = useCallback(
    async (runId: string) => {
      if (!runId) {
        setErrors([])
        clearErrors(fieldName)
        return
      }

      setIsValidating(true)
      const collected: string[] = []

      // Step 1: fetch run group — fail fast if not found or errored
      const runGroupResult = await triggerGetRunGroup(runId, true)
      const runGroupData = runGroupResult.data
      const runGroupError = !!runGroupResult.error

      if (runGroupError || runGroupData?.status === RunGroupStatus.ERROR) {
        collected.push("Run group not found")
        setErrors(collected)
        setError(fieldName, { message: JSON.stringify(collected) })
        setIsValidating(false)
        return
      }

      // Step 2: fetch run result — fail fast if not done-success
      const runGroupId = Object.keys(runGroupData?.runs ?? {})[0] ?? ""
      const runResultResult = await triggerGetRunResult(runGroupId, true)
      const runResultData = runResultResult.data
      const runResultError = !!runResultResult.error

      if (runResultError || runResultData?.status !== "done-success") {
        collected.push("Run did not complete successfully")
        setErrors(collected)
        setError(fieldName, { message: JSON.stringify(collected) })
        setIsValidating(false)
        return
      }

      // Step 3: fetch model (non-blocking — continue even on error)
      const modelKey = runGroupData?.runs[runGroupId]?.model_key ?? ""
      const modelResult = await triggerGetModel(modelKey, true)
      const modelData = modelResult.data
      const modelError = !!modelResult.error

      if (modelError) {
        collected.push("Could not load model for this run")
      }

      // Graph key compatibility
      if (!isBaseGraphKeyCompatible(runGroupData?.base_graph_key, graphKey)) {
        collected.push(
          `Graph key mismatch — run belongs to "${runGroupData?.base_graph_key ?? "unknown"}"`,
        )
      }

      // Sample count
      const formValues = getValues()
      if (runResultData?.num_samples !== +(formValues.number_of_samplings ?? 0)) {
        collected.push(
          `Sample count mismatch — previous run has ${
            runResultData?.num_samples ?? "unknown"
          } samples`,
        )
      }

      // Date range
      const modelStartTimestep = modelData?.timesteps?.[0]
      const modelEndTimestep = modelData?.timesteps?.[1]

      const currentDates = compareLinkedGraphDates(
        {
          startDate: formValues.start_date,
          endDate: formValues.end_date,
          simulationFrequency: graph.simulation_frequency,
          yearlyRollingStartMonth: graph.yearly_rolling_start_month,
          rollingDuration: graph.rolling_duration,
          maxLag,
        },
        {
          startDate: modelStartTimestep ? parseModelDate(modelStartTimestep) : undefined,
          endDate: modelEndTimestep ? parseModelDate(modelEndTimestep) : undefined,
        },
      )

      if (currentDates.errors.startDateMissing) collected.push("Missing start date")
      if (currentDates.errors.startDateMismatch) {
        collected.push(
          `Start date (${currentDates.current.start ?? "?"}) must match previous run (${
            currentDates.selected.start ?? "?"
          })`,
        )
      }
      if (currentDates.errors.endDateMissing) collected.push("Missing end date")
      if (currentDates.errors.endDateOutOfRange) {
        collected.push(
          `End date (${
            currentDates.current.end ?? "?"
          }) must be on or before previous run end date (${currentDates.selected.end ?? "?"})`,
        )
      }

      setErrors(collected)

      if (collected.length) {
        setError(fieldName, { message: JSON.stringify(collected) })
      } else {
        clearErrors(fieldName)
      }

      setIsValidating(false)
    },
    [
      fieldName,
      graphKey,
      graph,
      maxLag,
      getValues,
      setError,
      clearErrors,
      triggerGetRunGroup,
      triggerGetRunResult,
      triggerGetModel,
    ],
  )

  const validate = useCallback(
    (runId: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => runValidation(runId), DEBOUNCE_MS)
    },
    [runValidation],
  )

  const validateImmediate = useCallback(
    (runId: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      runValidation(runId)
    },
    [runValidation],
  )

  return { errors, isValidating, validate, validateImmediate }
}
